import React, { useEffect, useState, useMemo } from "react";
import { useAtom } from "jotai";
import CompanyListCell from "./CompanyListCell";
import "~/css/reception/CompanyList.css";
import { usePlayVoiceWithCooldown } from "~/components/hooks/usePlayVoiceWithCooldown";
import { languageAtom } from "~/Atom";
import { getPhoneBooksEntity, getReceptionCommonWord } from "~/utils/receptionDB";
import { getReceptionCommonWordsByLanguage, mapReceptionLanguage } from "~/utils/receptionUtils";
import type { GroupEntity, ReceptionCommonWordEntity } from "~/utils/receptionDB.types";

type Props = {
  onSelectCompany: (group: GroupEntity) => void;
};

/**
 * 会社一覧を表示し、見出し文言の取得と音声案内を行い、戻る操作と会社選択をコールバックする
 */
const CompanyList: React.FC<Props> = ({ onSelectCompany }) => {
  const [companies, setCompanies] = useState<GroupEntity[]>([]);
  const [language] = useAtom(languageAtom);
  const { playWithCooldown } = usePlayVoiceWithCooldown({
    cooldownMs: 2000,
  });
  const [receptionCommonWord, setReceptionCommonWord] = useState<
    ReceptionCommonWordEntity | undefined
  >(undefined);

  const headerTitle = useMemo((): string => {
    return (
      getReceptionCommonWordsByLanguage(receptionCommonWord, language)?.companyListHeaderTitle ?? ""
    );
  }, [receptionCommonWord, language]);

  useEffect(() => {
    const fetchReceptionCommonWords = async () => {
      try {
        const currentWord = await getReceptionCommonWord();
        setReceptionCommonWord(currentWord);
      } catch (err) {
        console.error("Failed to load reception common word:", err);
      }
    };

    void fetchReceptionCommonWords();
  }, []);

  const [isSelectCompanyEnabled, setIsSelectCompanyEnabled] = useState<boolean | null>(null);
  const [groups, setGroups] = useState<GroupEntity[]>([]);

  useEffect(() => {
    const fetchPhoneBook = async () => {
      try {
        const phoneBook = await getPhoneBooksEntity();
        setGroups(phoneBook?.groupList ?? []);
        setIsSelectCompanyEnabled(phoneBook?.isSelectCompanyEnabled ?? false);
      } catch (err) {
        console.error("Failed to load phone book data:", err);
        setGroups([]);
        setIsSelectCompanyEnabled(false);
      }
    };

    fetchPhoneBook();
  }, []);

  useEffect(() => {
    if (!isSelectCompanyEnabled) return;

    void playWithCooldown(headerTitle).catch((error) => {
      console.error("companyList playVoice failed:", error);
    });
  }, [isSelectCompanyEnabled, playWithCooldown]);

  useEffect(() => {
    try {
      const filtered = groups
        .filter((group) => group.parentGroupId === null)
        .sort((a, b) => a.order - b.order);

      setCompanies(filtered);
    } catch (err) {
      console.error("Parse error:", err);
    }
  }, [groups]);

  // Prefer the active language, then fall back to Japanese and English to avoid empty labels.
  const getName = (group: GroupEntity) => {
    const langCode = mapReceptionLanguage(language);

    const current = group.names.find((n) => n.languageCode === langCode);

    if (current?.name) return current.name;

    const ja = group.names.find((n) => n.languageCode === "ja");
    if (ja?.name) return ja.name;

    const en = group.names.find((n) => n.languageCode === "en");
    return en?.name || "";
  };

  return (
    <div className="company-list-screen">
      <div className="company-list-screen__text">
        <h2 className="company-list-screen__title">{headerTitle}</h2>
      </div>

      <div className="company-list-screen__body">
        <div className="company-list-screen__scroll">
          <div className="company-list-screen__grid-wrap">
            <div className="company-grid">
              {companies.map((item) => (
                <CompanyListCell
                  key={item.groupId}
                  name={getName(item)}
                  onClick={() => onSelectCompany(item)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyList;
